import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { CloseOutlined, QuestionOutlined } from '@ant-design/icons';

interface KeyboardShortcutsModalProps {
  open: boolean;
  onClose: () => void;
  currentPage?: string;
}

interface ShortcutGroup {
  title: string;
  shortcuts: ShortcutItem[];
}

interface ShortcutItem {
  description: string;
  keys: string[];
  context?: string; // e.g. 'orders', 'create', etc.
}

const Keycap = ({ keyLabel }: { keyLabel: string }) => (
  <Chip
    label={keyLabel}
    size="small"
    sx={{
      fontFamily: 'monospace',
      fontWeight: 500,
      fontSize: '0.83em', // another 10% smaller
      bgcolor: 'grey.100',
      border: '1px solid',
      borderColor: 'grey.300',
      mx: 0.25,
      height: 28,
      minWidth: 32,
      textAlign: 'center',
      color: 'text.primary',
      boxShadow: 1
    }}
    variant="outlined"
  />
);

const shortcutGroups: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { description: 'Show keyboard shortcuts', keys: ['Ctrl', '?'] },
      { description: 'Cancel / Close dialog', keys: ['Escape'] },
      { description: 'Go to Create/Edit page (from Orders Page)', keys: ['Ctrl', 'N'], context: 'orders' },
      { description: 'Go to Orders list', keys: ['Ctrl', 'L'], context: 'create' },
    ]
  },
  {
    title: 'Orders Page',
    shortcuts: [
      { description: 'Search orders', keys: ['Ctrl', 'F'], context: 'orders' },
      { description: 'Create new order', keys: ['Ctrl', 'N'], context: 'orders' },
    ]
  },
  {
    title: 'Create/Edit Page',
    shortcuts: [
      { description: 'Create new order (from Create/Edit Page)', keys: ['Ctrl', 'N'], context: 'create' },
      { description: 'Update order', keys: ['Ctrl', 'U'], context: 'edit' },
      { description: 'View Templates', keys: ['Ctrl', 'T'], context: 'create,edit' },
      { description: 'View Orders', keys: ['Ctrl', 'O'], context: 'create,edit' },
    ]
  },
  {
    title: 'Built-in Browser Commands',
    shortcuts: [
      { description: 'Hard reload (bypass cache)', keys: ['Ctrl', 'R'] },
      { description: 'Zoom in', keys: ['Ctrl', '+'] },
      { description: 'Zoom out', keys: ['Ctrl', '-'] },
      { description: 'Reset zoom to 100%', keys: ['Ctrl', '0'] },
    ]
  },
];

function contextNote(context?: string) {
  if (!context) return '';
  if (context === 'orders') return ' (Orders Page only)';
  if (context === 'create') return ' (Create Page only)';
  if (context === 'edit') return ' (Edit Page only)';
  if (context === 'create,edit') return ' (Create/Edit Page only)';
  return '';
}

export default function KeyboardShortcutsModal({ open, onClose, currentPage }: KeyboardShortcutsModalProps) {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  // Robust onClose handler for Dialog
  const handleDialogClose = (_event: object, reason?: string) => {
    if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
      onClose();
    }
  };

  // 2-column layout: left = Navigation + Built-in Browser Commands, right = Orders Page + Create/Edit Page
  const leftGroups = [shortcutGroups[0], shortcutGroups[3]];
  const rightGroups = [shortcutGroups[1], shortcutGroups[2]];

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          // Mobile: full screen with margins
          ...(downLG && {
            maxHeight: 'calc(100vh - 50px)', // 25px margin top and bottom
            minHeight: 'calc(100vh - 50px)',
            m: '25px',
            borderRadius: 2,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transform: 'none',
          }),
          // Desktop: centered modal
          ...(!downLG && {
            maxHeight: '80vh',
            minHeight: '480px',
            borderRadius: 2.5,
            top: 'unset',
            position: 'absolute',
            left: '50%',
            transform: 'translate(-50%, -30%)', // move up 30%
          }),
          p: 0,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
          fontSize: '1.25rem', // 25% smaller than previous
          minHeight: 64,
        }}
      >
        <QuestionOutlined style={{ fontSize: 36, marginRight: 8 }} />
        <Typography component="span" sx={{ flex: 1, fontSize: '1.25rem', fontWeight: 700 }}>
          Keyboard shortcuts
        </Typography>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          <Keycap keyLabel="Ctrl" />
          <Keycap keyLabel="?" />
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 4, pb: 2, px: 4, fontSize: '1.2rem' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Stack spacing={4}>
              {leftGroups.map((group, groupIdx) => (
                <Box key={group.title + groupIdx}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 600, fontSize: '1.25rem' }}>
                    {group.title}
                  </Typography>
                  <Stack spacing={1.5}>
                    {group.shortcuts.map((shortcut, idx) => {
                      const isContextual = !!shortcut.context;
                      const isActive = !isContextual || 
                        (shortcut.context && shortcut.context.split(',').includes(currentPage || ''));
                      return (
                        <Box
                          key={shortcut.description + idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            opacity: isActive ? 1 : 0.5,
                            fontSize: '1.1em',
                          }}
                        >
                          <Typography variant="body2" sx={{ pr: 2, flex: 1, fontSize: '0.73em' }}>
                            {shortcut.description}
                            {!isActive && (
                              <Typography component="span" variant="caption" color="text.secondary">
                                {contextNote(shortcut.context)}
                              </Typography>
                            )}
                          </Typography>
                          <Box>
                            {shortcut.keys.map((k, i) => <Keycap key={k + i} keyLabel={k} />)}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={4}>
              {rightGroups.map((group, groupIdx) => (
                <Box key={group.title + groupIdx}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 600, fontSize: '1.25rem' }}>
                    {group.title}
                  </Typography>
                  <Stack spacing={1.5}>
                    {group.shortcuts.map((shortcut, idx) => {
                      const isContextual = !!shortcut.context;
                      const isActive = !isContextual || 
                        (shortcut.context && shortcut.context.split(',').includes(currentPage || ''));
                      return (
                        <Box
                          key={shortcut.description + idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            opacity: isActive ? 1 : 0.5,
                            fontSize: '1.1em',
                          }}
                        >
                          <Typography variant="body2" sx={{ pr: 2, flex: 1, fontSize: '0.73em' }}>
                            {shortcut.description}
                            {!isActive && (
                              <Typography component="span" variant="caption" color="text.secondary">
                                {contextNote(shortcut.context)}
                              </Typography>
                            )}
                          </Typography>
                          <Box>
                            {shortcut.keys.map((k, i) => <Keycap key={k + i} keyLabel={k} />)}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between', px: 4, py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.1em' }}>
          Shortcuts work everywhere unless otherwise noted. Some may be handled by your browser or desktop app.
        </Typography>
        {/* If you re-enable the Close button, font size is reduced by 10% */}
        {/*
        <Button onClick={onClose} startIcon={<CloseOutlined />} sx={{ fontSize: '0.9em' }}>
          Close
        </Button>
        */}
      </DialogActions>
    </Dialog>
  );
} 